import React, { FC, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

import parse from "autosuggest-highlight/parse";

import { AddressAutocompleteParameters, FormFieldTextAddressInput } from "stentor-models";

import { FormTextInputProps } from "../FormFieldTextInput/FormFieldTextInput";
import { AddressAutocompleteService, PlaceType } from "../../utils/AddressAutocompleteService";

import styles from "./FormFieldTextInputAddress.module.scss";

const POWERED_BY_GOOGLE_URL =
    "https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png";

// How long typing has to pause before we ask for predictions, as MUI's Google Maps demo did.
const DEBOUNCE_MS = 400;

type PredictionRequest = { input: string; params?: AddressAutocompleteParameters };
type PredictionCallback = (results?: readonly PlaceType[]) => void;

/**
 * Trailing-edge debounce with a `clear`, the part of `@mui/material/utils`'s debounce we used.
 */
function debounce<A extends unknown[]>(fn: (...args: A) => void, wait: number) {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const debounced = (...args: A) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
    };
    debounced.clear = () => clearTimeout(timeout);
    return debounced;
}

// MUI's LocationOn icon
const LocationOnIcon: FC = () => (
    <svg
        className={styles.xapp_address_option_icon}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
    >
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7m0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5" />
    </svg>
);

export interface FormFieldTextInputAddressProps
    extends FormTextInputProps,
        Pick<FormFieldTextAddressInput, "googleMapsApiKey" | "mapsBaseUrl"> {
    readonly field: FormFieldTextAddressInput;
    /**
     * When true, the user must select an address from the autocomplete suggestions.
     * Free-text entry is not allowed.
     *
     * Can also be set via `field.requireSelection` (field-level takes precedence).
     * @see https://github.com/stentorium/stentor/issues/3040
     */
    readonly requireSelection?: boolean;
}

/**
 * An address field with Places suggestions: a WAI-ARIA combobox (an input plus a listbox), native
 * since XappMedia/chat-widget#1744. It behaves as the MUI Autocomplete it replaced did.
 */
export const FormFieldTextInputAddress: FC<FormFieldTextInputAddressProps> = (props) => {
    const {
        field,
        onFieldChange,
        errorMessage,
        required,
        googleMapsApiKey,
        mapsBaseUrl,
        requireSelection,
    } = props;

    // Field-level takes precedence, then component prop, default false
    const isFreeSolo = !(field.requireSelection ?? requireSelection);

    const [address, setAddress] = useState<PlaceType | null>(null);
    const [text, setText] = useState("");
    const [options, setOptions] = useState<readonly PlaceType[]>([]);
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    // -1 is the text itself: nothing in the list is active
    const [activeIndex, setActiveIndex] = useState(-1);

    const id = `xapp-address-${useId()}`;
    const listboxId = `${id}-listbox`;
    const helperId = `${id}-helper-text`;
    const optionId = (index: number) => `${id}-option-${index}`;

    const listboxRef = useRef<HTMLUListElement>(null);

    // Mirrors the current input text so async place details callbacks can tell
    // whether the customer has typed or reselected since the request was made.
    const textRef = useRef("");

    const onTextChange = (t: string) => {
        textRef.current = t;
        setText(t);
    };

    useEffect(() => {
        onFieldChange(field.name, text);
    }, [field.name, onFieldChange, text]);

    const apiKey = field.googleMapsApiKey || googleMapsApiKey;
    const baseUrl = field.mapsBaseUrl || mapsBaseUrl;

    const autocompleteService = useMemo(
        () => new AddressAutocompleteService({ apiKey, baseUrl }),
        [apiKey, baseUrl]
    );

    // Show Google attribution when using the xapp.ai proxy (which forwards to Google Places API).
    // Custom non-xapp.ai base URLs are assumed to be non-Google backends that handle their own attribution.
    const showGoogleAttribution = (() => {
        if (!baseUrl) return true;
        try {
            const hostname = new URL(baseUrl).hostname;
            return hostname === "xapp.ai" || hostname.endsWith(".xapp.ai");
        } catch {
            return false;
        }
    })();

    const fetch = useMemo(
        () =>
            debounce((request: PredictionRequest, callback: PredictionCallback) => {
                autocompleteService.getPlacePredictions(request, callback);
            }, DEBOUNCE_MS),
        [autocompleteService]
    );

    // A request still waiting out the debounce must not fire after the field is gone
    useEffect(() => () => fetch.clear(), [fetch]);

    useEffect(() => {
        let active = true;

        if (text === "") {
            setOptions([]);
            return undefined;
        }

        const queryParams = field.mapsUrlQueryParams || undefined;

        fetch({ input: text, params: queryParams }, (results?: readonly PlaceType[]) => {
            // A response for text the customer has since changed is dropped
            if (active) {
                setOptions(results || []);
            }
        });

        return () => {
            active = false;
        };
    }, [text, fetch, field.mapsUrlQueryParams]);

    // The option already chosen is not offered again (MUI's filterSelectedOptions)
    const visibleOptions = useMemo(
        () => (address ? options.filter((o) => o.description !== address.description) : options),
        [options, address]
    );

    // A free-text field has nothing to say when there are no suggestions; one that requires a
    // selection says so.
    const popupOpen = open && (visibleOptions.length > 0 || (!isFreeSolo && text !== ""));

    // New suggestions reset the active option, and it can never point past the list
    useEffect(() => {
        setActiveIndex(-1);
    }, [visibleOptions]);

    // Keep the active option in view inside the list without scrolling the page around it
    useLayoutEffect(() => {
        const listbox = listboxRef.current;
        const option = activeIndex >= 0 ? listbox?.children[activeIndex] : undefined;
        if (!listbox || !(option instanceof HTMLElement)) {
            return;
        }
        if (option.offsetTop < listbox.scrollTop) {
            listbox.scrollTop = option.offsetTop;
        } else if (
            option.offsetTop + option.offsetHeight >
            listbox.scrollTop + listbox.clientHeight
        ) {
            listbox.scrollTop = option.offsetTop + option.offsetHeight - listbox.clientHeight;
        }
    }, [activeIndex, popupOpen]);

    const close = () => {
        setOpen(false);
        setActiveIndex(-1);
    };

    const select = (option: PlaceType) => {
        close();
        setAddress(option);
        onTextChange(option.description);
        // Resolve the selected prediction to full place details so the displayed
        // and stored address includes the postal code (predictions never carry one).
        if (option.placeId) {
            const { placeId, description } = option;
            autocompleteService.getPlaceDetails(placeId, (details) => {
                // Skip stale results: only apply if the field still shows this prediction
                if (details?.formattedAddress && textRef.current === description) {
                    setAddress({ ...option, description: details.formattedAddress });
                    onTextChange(details.formattedAddress);
                }
            });
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        onTextChange(value);
        if (value === "") {
            // Clearing the text clears the selection too, as MUI did
            setAddress(null);
        } else {
            setOpen(true);
        }
    };

    // Up/Down cycle through the options and back to the text itself (MUI's includeInputInList)
    const move = (step: 1 | -1) => {
        const count = visibleOptions.length;
        if (count === 0) {
            return;
        }
        setActiveIndex((current) => {
            const next = current + step;
            if (next >= count) return -1;
            if (next < -1) return count - 1;
            return next;
        });
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case "ArrowDown":
            case "ArrowUp":
                e.preventDefault();
                setOpen(true);
                move(e.key === "ArrowDown" ? 1 : -1);
                break;
            case "Enter":
                if (popupOpen && activeIndex >= 0 && visibleOptions[activeIndex]) {
                    // Choosing a suggestion must not also submit the form
                    e.preventDefault();
                    select(visibleOptions[activeIndex]);
                } else {
                    close();
                }
                break;
            case "Escape":
                if (popupOpen) {
                    // Closes the list, not the dialog the form sits in
                    e.preventDefault();
                    e.stopPropagation();
                    close();
                }
                break;
            default:
                break;
        }
    };

    const onBlur = () => {
        setFocused(false);
        close();
        // A field that requires a selection drops text that isn't one (MUI's clearOnBlur)
        if (!isFreeSolo) {
            const chosen = address?.description || "";
            if (text !== chosen) {
                onTextChange(chosen);
            }
        }
    };

    // MUI's floating label: resting inside the field until it is focused or has a value. With no
    // label to collide with, the placeholder is always shown.
    const shrink = focused || text.length > 0 || !field.label;
    const activeOption = popupOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined;

    return (
        <div
            className={`${styles.xapp_text_input_address} ${
                errorMessage ? styles.xapp_address_error : ""
            }`}
            style={field.style}
        >
            <div className={styles.xapp_address_anchor}>
                <div
                    className={`${styles.xapp_address_root} ${
                        focused ? styles.xapp_address_focused : ""
                    }`}
                >
                    {field.label && (
                        <label
                            htmlFor={id}
                            className={styles.xapp_address_label}
                            data-shrink={shrink ? "true" : "false"}
                        >
                            {field.label}
                            {required && (
                                <span className={styles.xapp_address_asterisk} aria-hidden="true">
                                    {" *"}
                                </span>
                            )}
                        </label>
                    )}
                    <input
                        id={id}
                        name={field.name}
                        type="text"
                        className={styles.xapp_address_control}
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={popupOpen}
                        aria-controls={listboxId}
                        aria-activedescendant={activeOption}
                        aria-invalid={!!errorMessage}
                        aria-describedby={errorMessage ? helperId : undefined}
                        // The browser's own autofill list would cover the suggestions
                        autoComplete="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        placeholder={field.placeholder}
                        required={required}
                        maxLength={field.maxLength || undefined}
                        value={text}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        onFocus={() => setFocused(true)}
                        onBlur={onBlur}
                        onMouseDown={() => {
                            // A click opens the list, as MUI's did; it never toggles it shut
                            if (!popupOpen && text !== "") {
                                setOpen(true);
                            }
                        }}
                        // Read by the stylesheet, which hides the placeholder behind a resting label.
                        data-shrink={shrink ? "true" : "false"}
                    />
                </div>
                <div
                    className={styles.xapp_address_popup}
                    hidden={!popupOpen}
                    // Keeps the input focused while an option is clicked
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <ul
                        ref={listboxRef}
                        id={listboxId}
                        role="listbox"
                        aria-label={`${field.label || "Address"} suggestions`}
                        className={styles.xapp_address_listbox}
                    >
                        {visibleOptions.map((option, index) => {
                            const matches =
                                option.structured_formatting.main_text_matched_substrings || [];
                            const parts = parse(
                                option.structured_formatting.main_text,
                                matches.map((match) => [match.offset, match.offset + match.length])
                            );
                            return (
                                <li
                                    key={option.placeId || option.description}
                                    id={optionId(index)}
                                    role="option"
                                    aria-selected={index === activeIndex}
                                    className={`${styles.xapp_address_option} ${
                                        index === activeIndex
                                            ? styles.xapp_address_option_active
                                            : ""
                                    }`}
                                    onMouseMove={() => setActiveIndex(index)}
                                    onClick={() => select(option)}
                                >
                                    <LocationOnIcon />
                                    <span className={styles.xapp_address_option_text}>
                                        {parts.map((part, i) => (
                                            <span
                                                key={i}
                                                style={{
                                                    fontWeight: part.highlight ? "bold" : "normal",
                                                }}
                                            >
                                                {part.text}
                                            </span>
                                        ))}
                                        <span className={styles.xapp_address_secondary}>
                                            {option.structured_formatting.secondary_text}
                                        </span>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                    {popupOpen && visibleOptions.length === 0 && (
                        <div className={styles.xapp_address_no_options}>No locations</div>
                    )}
                    {showGoogleAttribution && (
                        <div className={styles.xapp_address_attribution}>
                            <img src={POWERED_BY_GOOGLE_URL} alt="powered by Google" />
                        </div>
                    )}
                </div>
            </div>
            {errorMessage && (
                <p id={helperId} className={styles.xapp_address_helper}>
                    {errorMessage}
                </p>
            )}
        </div>
    );
};
